import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { PerformanceService } from '../performance/performance/performance.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { FullScreenPreviewService } from 'src/app/utils/full-screen-preview/full-screen-preview.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { environment, environmentUS } from 'src/environments/index';
import { getCurrentEnv } from 'src/app/core/http/env-utils';

type ImgFile = File & { previewUrl?: string };

@Component({
  selector: 'nudge-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: [
    '../../recordings/recordings/recordings.component.css',
    '../../recordings/heatmaps/heatmaps.component.css',
    './ai-chat.component.css',
  ],
})
export class NudgeAiChatComponent implements OnInit, OnDestroy {
  constructor(
    private performanceService: PerformanceService,
    private route: ActivatedRoute,
    private location: Location,
    public dialog: MatDialog,
    private router: Router,
    private fullScreenPreviewService: FullScreenPreviewService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService
  ) {}
  private subs: Subscription = new Subscription();
  private currentAbort: AbortController | null = null;
  // Normalize base URL and compose API paths safely for Nudge endpoints
  private resolveApiUrl(path: string): string {
    const selectedEnvironment = getCurrentEnv();
    const baseRaw = (selectedEnvironment as any)?.address || '';
    const base = baseRaw.replace(/\/+$/, ''); // trim trailing slashes
    const baseNoApi = base.replace(/\/api$/, ''); // avoid double /api
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    return `${baseNoApi}/api${normalizedPath}`;
  }

  // --- Streaming Helpers (SSE/NDJSON/plain text) ---
  private handleStreamPayload(
    payload: any,
    push: (chunk: string) => void,
    onStatus: (statusKey: string) => void,
    onError: (err: any) => void
  ) {
    if (typeof payload === 'string') {
      if (payload === '[DONE]') return; // SSE termination
      push(payload);
      return;
    }
    // Ignore control markers (do not render them)
    if (payload && (payload.type === 'done' || payload.type === 'complete')) {
      return;
    }

    if (payload?.type === 'error') {
      onError(payload);
      return;
    }

    // Some backends send {error:{message}} or {error:"..."} without a type
    if (payload && (payload.error || payload?.error?.message)) {
      onError(payload);
      return;
    }

    // Handle status messages explicitly
    if (payload?.type === 'status' && payload?.status) {
      onStatus(payload.status);
      return;
    }

    // Otherwise, treat as content/token
    const piece = payload?.delta ?? payload?.content ?? payload?.token ?? '';
    if (piece) push(piece);
  }

  private streamStatusMap: Record<string, string> = {
    sending_message: 'Preparing your request...',
    starting_analysis: 'Starting analysis...',
    receiving_data: 'Receiving data...',
  };

  private consumeSSE(
    buffer: string,
    onPayload: (payload: any) => void
  ): { buffer: string; advanced: boolean } {
    let advanced = false;
    const events = buffer.split(/\r?\n\r?\n/);
    for (let i = 0; i < events.length - 1; i++) {
      const block = events[i];
      const lines = block.split(/\r?\n/).filter(Boolean);
      const dataLines = lines
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim());
      dataLines.forEach((data) => {
        advanced = true;
        if (!data) return;
        if (data === '[DONE]') {
          onPayload('[DONE]');
          return;
        }
        try {
          onPayload(JSON.parse(data));
        } catch {
          onPayload(data);
        }
      });
    }
    return { buffer: events[events.length - 1], advanced };
  }

  private consumeNDJSON(
    buffer: string,
    onPayload: (payload: any) => void
  ): { buffer: string; advanced: boolean } {
    let advanced = false;
    const lines = buffer.split(/\r?\n/);
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      advanced = true;
      try {
        onPayload(JSON.parse(line));
      } catch {
        onPayload(line);
      }
    }
    return { buffer: lines[lines.length - 1], advanced };
  }

  private async safeText(resp: Response): Promise<string> {
    try {
      return await resp.text();
    } catch {
      return '';
    }
  }

  private stripSources(s: string): string {
    // remove references like "【16:19†trace.json】", "【12:34†source】", etc. and stray asterisks
    return s.replace(/【\d+:\d+†[^\]]+】/g, '').replace(/\*/g, '');
  }

  private stopMessageLoaderIfAny() {
    try {
      this.messageLoaderLineIndex = 0;
      clearInterval(this.messageLoaderInterval);
    } catch {}
    this.sendMessageLoading = false;
    this.disableTextareaInput = false;
  }

  private startMessageLoaderIfAny() {
    this.disableTextareaInput = true;
    // this.sendMessageLoading = true;
    // this.messageLoaderFunc();
  }

  // Core streaming call for Nudge chat
  private async streamAskAI(
    formData: FormData,
    aiMessageRef: {
      message: string;
      isUser: boolean;
      createdAt: string;
      images: any[];
    }
  ) {
    // Use normalized API base from environments (handles trailing slashes and optional /api)
    const url = this.resolveApiUrl('/performanceOverview/ask-ai?stream=true');

    // Abort previous stream if any
    this.currentAbort?.abort();
    const controller = new AbortController();
    this.currentAbort = controller;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: 'token:' + this.localStorageService.get('auth_key'),
          Accept: 'text/event-stream',
        },
        body: formData,
        signal: controller.signal,
      });

      if (!resp.ok) {
        const text = await this.safeText(resp);
        throw new Error(`HTTP ${resp.status}: ${text || 'Request failed'}`);
      }

      // If server did NOT stream and replied with JSON, handle it like the old non-stream flow.
      const ctype = resp.headers.get('content-type') || '';
      if (
        ctype.includes('application/json') &&
        !ctype.includes('event-stream')
      ) {
        try {
          const json = await resp.json();
          // Replace the placeholder with the final answer
          aiMessageRef.message = this.stripSources(
            json?.assistantMessage?.content?.[0]?.text?.value ||
              json?.assistantMessage?.message ||
              json?.message ||
              JSON.stringify(json)
          );
          // Keep raw content up-to-date for future re-renders when assistantMessage exists
          if (json?.assistantMessage)
            this.chatContentRaw.unshift(json.assistantMessage);
        } catch {
          aiMessageRef.message = 'Sorry — something went wrong (invalid JSON).';
        }
        // Stop loaders and short-circuit streaming path
        this.stopMessageLoaderIfAny();
        return;
      }

      if (!resp.body || !('getReader' in resp.body)) {
        // Fallback – no stream support: let the existing non-stream path handle it
        throw new Error('Streaming not supported by this browser/response.');
      }

      // Begin streaming the body
      const reader = (resp.body as ReadableStream<Uint8Array>).getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      let stopStreaming = false;
      let currentStatus: string | null = null;
      const push = (chunk: string) => {
        if (stopStreaming) return;
        if (!chunk) return;
        // If we were showing a status, clear it on first real content
        if (currentStatus !== null) {
          aiMessageRef.message = '';
          currentStatus = null;
        }
        if (
          aiMessageRef.message === 'Thinking...' ||
          aiMessageRef.message.includes('Thinking')
        ) {
          aiMessageRef.message = '';
        }
        aiMessageRef.message += this.stripSources(chunk);
        // keep the UI pinned to bottom as tokens arrive
        this.scrollToBottom('auto');
      };

      const onStatus = (statusKey: string) => {
        const mapped = this.streamStatusMap[statusKey] || statusKey;
        currentStatus = mapped;
        aiMessageRef.message = mapped;
        this.scrollToBottom('auto');
      };

      const onError = (errPayload: any) => {
        // Try to extract a helpful message
        const msg =
          errPayload?.message ||
          errPayload?.error?.message ||
          errPayload?.detail ||
          errPayload?.error ||
          'Something went wrong! Please try again.';

        console.error('Stream error payload:', errPayload);
        stopStreaming = true;
        // Abort the underlying fetch/reader immediately to stop more chunks
        try {
          controller.abort();
        } catch {}
        currentStatus = null;
        aiMessageRef.message = String(msg);
        this.stopMessageLoaderIfAny();
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (stopStreaming) break;

        buffer += decoder.decode(value, { stream: true });

        let advanced = false;
        ({ buffer, advanced } = this.consumeSSE(buffer, (p) =>
          this.handleStreamPayload(p, push, onStatus, onError)
        ));
        if (!advanced) {
          ({ buffer, advanced } = this.consumeNDJSON(buffer, (p) =>
            this.handleStreamPayload(p, push, onStatus, onError)
          ));
        }
        if (!advanced && buffer.length > 2048) {
          push(buffer);
          buffer = '';
        }
      }

      // Guard: if streaming was stopped due to error, skip final parsing
      if (stopStreaming) {
        buffer = '';
        return;
      }

      // Final drain: parse any remaining SSE/NDJSON blocks.
      let finalAdvanced = false;
      ({ buffer, advanced: finalAdvanced } = this.consumeSSE(buffer, (p) =>
        this.handleStreamPayload(p, push, onStatus, onError)
      ));
      if (!finalAdvanced) {
        ({ buffer, advanced: finalAdvanced } = this.consumeNDJSON(buffer, (p) =>
          this.handleStreamPayload(p, push, onStatus, onError)
        ));
      }
      const rest = buffer.trim();
      // Only push if it's plain text (avoid JSON control like {type:"complete"})
      if (
        !finalAdvanced &&
        rest &&
        !rest.startsWith('{') &&
        !rest.startsWith('[')
      ) {
        push(rest);
      }
      buffer = '';
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // user cancelled; keep partial text
        console.warn('Stream aborted by user.');
      } else {
        console.error(err);
        if (aiMessageRef.message === 'Thinking...' || !aiMessageRef.message) {
          aiMessageRef.message =
            'Sorry — something went wrong while streaming.';
        }
      }
    } finally {
      this.currentAbort = null;
      this.stopMessageLoaderIfAny();
    }
  }

  public cancelCurrentAI() {
    this.currentAbort?.abort();
  }

  confirmationDialogRef: MatDialogRef<ConfirmationComponent>;

  firstLoader: boolean = true;
  isLoading: boolean = false;
  isChatLoading: boolean = false;
  analysesHistory: any = [];
  totalItems: number = 0;

  selectedChatObj: any = {};
  target: string = '';
  chatID: string = '';

  chatContentRaw: any = [];
  chatContent: any = [];
  chatInput: string = '';
  constructedChatContent: any = [];

  darkMode: boolean = false;

  params_constructed: any = {};
  showPlaceholderWithFilters: boolean = false;

  sendMessageLoading: boolean = false;

  messageLoaderInterval: any;
  messageLoaderLineIndex: number = 0;
  loaderMessages = [
    'Processing your request…',
    'Preparing your analysis…',
    'Generating your response…',
    'Organizing your results…',
    'Working on your prompt…',
    'Gathering insights…',
    'Reviewing your input…',
    'Analyzing data…',
    'Collecting information…',
    'Structuring your output…',
    'Crafting your response…',
    'Compiling relevant details…',
    'Checking available data…',
    'Finding relevant information…',
    'Refining your request…',
    'Aligning your data…',
    'Assembling your response…',
    'Summarizing key points…',
    'Loading your data…',
    'Reviewing details…',
    'Processing input…',
    'Organizing content…',
    'Generating insights…',
    'Formulating your output…',
    'Thinking through your request…',
    'Connecting the dots…',
    'Clarifying context…',
    'Preparing details…',
    'Arranging information…',
    'Polishing your response…',
    'Working on results…',
    'Gathering key data…',
    'Reviewing context…',
    'Analyzing your input…',
    'Retrieving information…',
    'Summarizing insights…',
    'Refining output…',
    'Wrapping up your response…',
    'Almost ready…',
    'Finalizing details…',
  ];
  disableTextareaInput: boolean = false;

  ngOnInit(): void {
    const darkMode = this.localStorageService.get('darkMode');
    if (darkMode === true) {
      document.body.classList.add('dark-theme');
      this.darkMode = true;
    } else if (darkMode === false) {
      document.body.classList.remove('dark-theme');
      this.darkMode = false;
    } else {
      const deviceMode = window.matchMedia('(prefers-color-scheme: dark)');
      if (deviceMode.matches) {
        document.body.classList.add('dark-theme');
        this.localStorageService.set('darkMode', true);
        this.darkMode = true;
      }
    }

    // this.recordingParams = JSON.parse(
    //   sessionStorage.getItem('recordingParamObj') || '{}'
    // );

    // this.constructRecordingParamObj();

    this.route.params.subscribe((params) => {
      this.target = params['chatTarget'];
      this.chatID = params['id'];

      this.getAIChatHistory();
      this.getAIChat();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    sessionStorage.removeItem('recordingParamObj');
  }

  formatDate(date: string) {
    return new Date(date).toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      day: 'numeric',
      month: 'short',
    });
  }

  constructRecordingParamObj() {
    try {
      this.params_constructed = {
        startDate: this.formatDate(
          this.selectedChatObj?.params?.startDate?.split('@')[0]
        ),
        endDate: this.formatDate(
          this.selectedChatObj?.params?.endDate?.split('@')[0]
        ),
        filters: this.getActiveFilters(),
      };
      this.showPlaceholderWithFilters = true;
    } catch (e) {
      this.showPlaceholderWithFilters = false;
      console.log(e);
    }
  }

  truncateText(
    text: string | null | undefined,
    maxLength: number = 50
  ): string {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  }

  constructAIChatHistory(data: any) {
    this.isLoading = false;
    this.firstLoader = false;

    if (!data || data.length === 0) {
      this.analysesHistory = [];
      this.selectedChatObj = {};
      return;
    }

    this.analysesHistory = data;
    // this.totalItems = data.length;

    try {
      this.analysesHistory.forEach((analysis: any) => {
        analysis.updatedAt = this.formatDate(analysis.updatedAt);
      });
    } catch (err) {
      this.notificationService.info('Something went wrong!');
      console.log(err);
    }

    this.selectedChatObj = this.analysesHistory.find(
      (analysis: any) => analysis._id === this.chatID
    );

    this.constructRecordingParamObj();
  }

  getAIChatHistory() {
    this.isLoading = true;

    this.subs.add(
      this.performanceService
        .getAIChatHistory({ target: this.target })
        .subscribe({
          next: (res) => {
            if (!res || res?.length === 0) {
              this.goBackToDashboard();
              return;
            }
            this.constructAIChatHistory(res);
          },
          error: (err) => {
            this.goBackToDashboard();
            this.notificationService.info('Something went wrong!');
          },
        })
    );
  }

  addStrongTags(text: string) {
    // Replace **bold** with <strong>
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Replace ###### Heading with <h6>
    text = text.replace(/^###### (.+)$/gm, '<h6>$1</h6>');

    // Replace ##### Heading with <h5>
    text = text.replace(/^##### (.+)$/gm, '<h5>$1</h5>');

    // Replace #### Heading with <h4>
    text = text.replace(/^#### (.+)$/gm, '<h4>$1</h4>');

    // Replace ### Heading with <h3>
    text = text.replace(/^### (.+)$/gm, '<h3>$1</h3>');

    // Replace ## Heading with <h2>
    text = text.replace(/^## (.+)$/gm, '<h2>$1</h2>');

    // Replace # Heading with <h1>
    text = text.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    return text;
  }

  constructAIChat() {
    this.isChatLoading = false;
    this.constructedChatContent = [];

    if (
      !this.chatContentRaw ||
      this.chatContentRaw.length === 0 ||
      typeof this.chatContentRaw === 'string'
    ) {
      return;
    }

    this.chatContentRaw.forEach((message: any) => {
      const messageText =
        message.content.filter((message: any) => {
          return message.type === 'text';
        })?.[0]?.text?.value || '';

      const images =
        message.content
          .filter((message: any) => {
            return message.type === 'image_url';
          })
          ?.map((message: any) => {
            return message.image_url;
          }) || [];

      let parsedMessageText = this.addStrongTags(
        this.stripSources(messageText)
      );

      this.constructedChatContent.push({
        message: this.stripSources(parsedMessageText),
        isUser: message.role === 'user',
        createdAt: this.formatDate(message.created_at),
        images: images,
      });
    });

    this.constructedChatContent.reverse();
  }

  getAIChat(behavior: ScrollBehavior = 'smooth') {
    this.isChatLoading = true;
    this.chatContentRaw = [];

    if (this.files.length > 0) {
      this.files.forEach(
        (f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl!)
      );
    }

    this.subs.add(
      this.performanceService
        .getAIChat({ target: this.target, id: this.chatID })
        .subscribe({
          next: (res) => {
            if (!res) {
              this.chatContentRaw = [];
              this.constructAIChat();
              this.notificationService.info('Something went wrong!');
              return;
            }

            if (res.status === 'Pending') {
              this.isChatLoading = false;
              this.sendMessage(true);
            } else {
              this.chatContentRaw = res;
              this.constructAIChat();
              this.scrollToBottom(behavior);
            }
          },
          error: (err) => {
            this.chatContentRaw = [];
            this.constructAIChat();
            this.notificationService.info('Something went wrong!');
          },
        })
    );
  }

  goBackToDashboard() {
    this.router.navigate(['/master-preview']);
  }

  selectAnalysis(analysis: any) {
    const currentPath = this.location.path();

    const [pathPart, queryString] = currentPath.split('?');

    const segments = pathPart.split('/');
    segments[segments.length - 1] = analysis._id;

    const updatedPath = segments.join('/');

    const updatedURL = queryString
      ? `${updatedPath}?${queryString}`
      : updatedPath;

    this.location.replaceState(updatedURL);

    this.chatID = analysis._id;

    this.selectedChatObj = analysis;
    this.constructRecordingParamObj();
    this.sendMessageLoading = false;

    this.getAIChat('auto');
  }

  onEnter(event: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.sendMessage();
    }
  }

  messageLoaderFunc() {
    this.messageLoaderInterval = setInterval(() => {
      if (this.messageLoaderLineIndex + 1 == this.loaderMessages.length) {
        clearInterval(this.messageLoaderInterval);
      } else {
        this.messageLoaderLineIndex++;
      }
    }, 6000);
  }

  sendMessage(bypassChatInput: boolean = false) {
    if (!bypassChatInput) {
      if (!this.chatInput || this.chatInput.length === 0) {
        return;
      }

      const userMessage = {
        role: 'user',
        content: [
          {
            type: 'text',
            text: {
              value: this.chatInput?.trim(),
            },
          },
          ...this.imagesToSend.map((file: any) => {
            return {
              type: 'image_url',
              image_url: {
                url: file.previewUrl,
              },
            };
          }),
        ],
        created_at: new Date().toISOString(),
      };

      this.constructedChatContent.push({
        message: this.chatInput?.trim(),
        isUser: true,
        createdAt: this.formatDate(new Date().toISOString()),
        images: [
          ...this.imagesToSend.map((file: any) => {
            return {
              type: 'image_url',
              url: file.previewUrl,
            };
          }),
        ],
      });

      this.chatContentRaw.unshift(userMessage);

      this.scrollToBottom();
    }

    // Prepare a streaming AI placeholder message in the UI
    const aiMessage = {
      message: 'Thinking...',
      isUser: false,
      createdAt: this.formatDate(new Date().toISOString()),
      images: [],
    };
    this.constructedChatContent.push(aiMessage);

    const params = {
      conversationId: this.chatID,
      target: this.selectedChatObj?.params?.target || this.target,
      prompt: this.chatInput,
    };

    const formData = this.buildAskAIFormData(params, this.imagesToSend);

    this.chatInput = '';
    this.clearImages();

    // Start optional loader text cycling (kept from existing UX)
    this.startMessageLoaderIfAny();

    // Try streaming first; if it fails for any reason, fall back to the existing non-stream endpoint
    (async () => {
      try {
        await this.streamAskAI(formData, aiMessage);
      } catch (e) {
        // Fallback to the existing non-stream request
        this.subs.add(
          this.performanceService.askAI(formData).subscribe({
            next: (res) => {
              this.stopMessageLoaderIfAny();

              try {
                this.messageLoaderLineIndex = 0;
                clearInterval(this.messageLoaderInterval);
              } catch (e) {}

              // Replace the placeholder with the final answer
              aiMessage.message = this.stripSources(
                res?.assistantMessage?.content?.[0]?.text?.value ||
                  res?.assistantMessage?.message ||
                  JSON.stringify(res?.assistantMessage || res || '')
              );

              // Keep raw content up-to-date for future re-renders
              this.chatContentRaw.unshift(res?.assistantMessage);
              this.constructAIChat();
            },
            error: (err) => {
              this.stopMessageLoaderIfAny();
              console.log(err);
              aiMessage.message = 'Sorry — something went wrong.';
            },
          })
        );
      }
    })();
  }

  adjustTextareaHeight(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto'; // reset to shrink if needed

    const maxHeight = 300; // adjust as you like

    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
  }

  toogleOpenClose(event: any) {
    event.target.closest('.heatmap-side-content').classList.toggle('close');
  }

  sidenavToogle: boolean = false;
  triggerSidenav() {
    if (window.innerWidth > 1200) {
      this.sidenavToogle = false;
      return;
    }
    this.sidenavToogle = !this.sidenavToogle;
  }

  getActiveFilters(): Array<{ type: string; label: string; value: string }> {
    const filters: Array<{ type: string; label: string; value: string }> = [];

    if (this.selectedChatObj?.params?.country) {
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(
          this.selectedChatObj?.params?.country
        );
        filters.push({
          type: 'country',
          label: 'Country',
          value:
            countryName || this.selectedChatObj?.params?.country || 'Unknown',
        });
      } catch (e) {
        filters.push({
          type: 'country',
          label: 'Country',
          value: this.selectedChatObj?.params?.country,
        });
      }
    }

    if (this.selectedChatObj?.params?.browser) {
      filters.push({
        type: 'browser',
        label: 'Browser',
        value: this.selectedChatObj?.params?.browser,
      });
    }

    if (this.selectedChatObj?.params?.nudge) {
      let value = '';

      const nudges = this.selectedChatObj?.params?.nudge?.split(',');

      if (nudges?.length > 1) {
        value = `${nudges?.length} nudges`;
      } else {
        value = String(nudges);
      }

      filters.push({
        type: 'nudge',
        label: 'Nudge',
        value: value,
      });
    }

    if (this.selectedChatObj?.params?.os) {
      filters.push({
        type: 'os',
        label: 'OS',
        value: this.selectedChatObj?.params?.os,
      });
    }

    if (this.selectedChatObj?.params?.locations?.length) {
      const label = this.selectedChatObj?.params?.exactMatch
        ? 'Exact URL'
        : 'URL Contains';
      const value =
        this.selectedChatObj?.params?.locations.length === 1
          ? this.selectedChatObj?.params?.locations[0]
          : `${this.selectedChatObj?.params?.locations.length} locations`;

      filters.push({
        type: 'visitedURLs',
        label,
        value,
      });
    }

    if (this.selectedChatObj?.params?.durationRange) {
      const [min, max] = this.selectedChatObj?.params?.durationRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max} minutes`;
      } else if (min) {
        value = `Min ${min} minutes`;
      } else if (max) {
        value = `Max ${max} minutes`;
      }

      filters.push({
        type: 'duration',
        label: 'Duration',
        value,
      });
    }

    if (this.selectedChatObj?.params?.pageRange) {
      const [min, max] = this.selectedChatObj?.params?.pageRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max}`;
      } else if (min) {
        value = `Min ${min}`;
      } else if (max) {
        value = `Max ${max}`;
      }

      filters.push({
        type: 'pages',
        label: 'Pages',
        value,
      });
    }

    if (this.selectedChatObj?.params?.actionRange) {
      const [min, max] = this.selectedChatObj?.params?.actionRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max}`;
      } else if (min) {
        value = `Min ${min}`;
      } else if (max) {
        value = `Max ${max}`;
      }

      filters.push({
        type: 'actions',
        label: 'Actions',
        value,
      });
    }

    if (this.selectedChatObj?.params?.error) {
      let value = '';
      if (this.selectedChatObj?.params?.error.includes('image_error')) {
        value = 'Images failed to load';
      } else {
        value = this.selectedChatObj?.params?.error;
      }

      filters.push({
        type: 'error',
        label: 'Error',
        value: this.truncateText(value, 20),
      });
    }

    return filters.filter(
      (filter) =>
        filter &&
        filter.value != null &&
        filter.type != null &&
        typeof filter.value === 'string'
    );
  }

  scrollToBottom(behavior: ScrollBehavior = 'smooth') {
    // scroll to bottom
    setTimeout(() => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages) {
        chatMessages.scrollTo({
          top: chatMessages.scrollHeight,
          behavior: behavior,
        });
      }
    }, 50);
  }

  deleteChat(chat: any) {
    this.confirmationDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: {
        header: 'Delete Chat',
        content: 'Are you sure you want to delete this chat?',
      },
    });

    this.confirmationDialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;

      this.performanceService
        .deleteAIChat({ conversationId: chat._id, target: this.target })
        .subscribe({
          next: (res) => {
            if (chat._id === this.chatID) {
              this.chatID = this.analysesHistory?.find(
                (analysis: any) => analysis._id !== chat._id
              )?._id;
              this.getAIChat();
            }
            this.getAIChatHistory();
          },
          error: (err) => {
            this.notificationService.info('Something went wrong!');
          },
        });
    });
  }

  switchThemeMode(event: any) {
    if (event.checked) {
      this.darkMode = true;
      document.body.classList.add('dark-theme');
      this.localStorageService.set('darkMode', true);
    } else {
      this.darkMode = false;
      document.body.classList.remove('dark-theme');
      this.localStorageService.set('darkMode', false);
    }
  }

  // images features - start
  readonly MAX_FILES = 10;
  readonly MAX_SIZE_MB = 15;

  dragOver = false;
  files: ImgFile[] = [];
  errors: string[] = [];

  /** expose current value to parent if you want to read directly */
  get value(): File[] {
    return this.files.slice();
  }

  /** call this from parent after successful send */
  clearImages() {
    // this.files.forEach(
    //   (f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl!)
    // );
    this.files = [];
    this.errors = [];
    this.imagesToSend = [];
  }

  // ——— input[type=file] picker ———
  onPickFiles(ev: Event) {
    const input = ev.target as HTMLInputElement;
    if (input.files?.length) this.addFiles(input.files);
    input.value = ''; // allow re-selecting same file
  }

  // ——— drag & drop ———
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = true;
  }
  onDragLeave() {
    this.dragOver = false;
  }
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = false;
    if (ev.dataTransfer?.files?.length) this.addFiles(ev.dataTransfer.files);
  }

  // ——— paste screenshots/images ———
  @HostListener('document:paste', ['$event'])
  onPaste(ev: ClipboardEvent) {
    const items = ev.clipboardData?.items;
    if (!items) return;
    const pasted: File[] = [];
    for (const it of items as any) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) pasted.push(f);
      }
    }
    if (pasted.length) this.addFiles(pasted as unknown as FileList);
  }

  removeAt(index: number) {
    const [removed] = this.files.splice(index, 1);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);

    this.imagesToSend = this.files.slice();
  }

  // ——— core add/validate ———
  private addFiles(list: FileList | File[]) {
    const errs: string[] = [];

    for (const raw of Array.from(list)) {
      if (this.files.length >= this.MAX_FILES) {
        errs.push(`Maximum ${this.MAX_FILES} images allowed.`);
        break;
      }

      const f = raw as ImgFile;

      if (!f.type.startsWith('image/')) {
        errs.push(`${f.name || 'file'}: not an image.`);
        continue;
      }
      if (f.size > this.MAX_SIZE_MB * 1024 * 1024) {
        errs.push(`${f.name || 'image'}: > ${this.MAX_SIZE_MB}MB.`);
        continue;
      }
      // simple de-dupe: name + size
      if (this.files.some((x) => x.name === f.name && x.size === f.size))
        continue;

      f.previewUrl = URL.createObjectURL(f);
      this.files.push(f);
    }

    this.errors = errs;

    this.imagesToSend = this.files.slice();
  }

  // in your existing chat page component .ts
  imagesToSend: File[] = [];

  async returnFormData() {
    // your existing message sending logic...
    const prompt = this.chatInput?.trim();
    if (!prompt && this.imagesToSend.length === 0) return;

    const fd = new FormData();
    fd.append('prompt', prompt || '');
    this.imagesToSend.forEach((f, i) =>
      fd.append('images', f, f.name || `image-${i}.png`)
    );

    // await this.api.post(fd).toPromise();

    return fd;
  }

  // Build ONE FormData with JSON + files
  private buildAskAIFormData(params: any, images: File[]): FormData {
    const fd = new FormData();

    // put params as JSON blob/string
    fd.append('conversationId', params.conversationId);
    fd.append('target', params.target);

    fd.append('prompt', params.prompt);

    const fieldNames = [
      'photo1',
      'photo2',
      'photo3',
      'photo4',
      'photo5',
      'photo6',
      'photo7',
      'photo8',
      'photo9',
      'photo10',
    ];
    images.slice(0, fieldNames.length).forEach((file, i) => {
      fd.append(
        file.name || fieldNames[i],
        file,
        file.name || `image-${i}.png`
      );
    });

    return fd;
  }

  public showScreenshot(img: any): void {
    if (img) {
      const content = {
        src: img,
        html: '',
        label: '',
        isHTML: false,
        showLabel: false,
      };

      this.fullScreenPreviewService.setContent(content);
      this.fullScreenPreviewService.openPreview();
    }
  }
  // images features - end
}
